<template>
    <form class="order-manage-workers-add" @submit.prevent="addWorker">
        <h4>Add Worker</h4>
        <Grid :gap-x="30">
            <Column :span="4">
                <FormField name="role" required label="Role" :error="error">
                    <CustomSelect
                        v-model="role"
                        :options="orderRoles"
                        placeholder="Select Role"
                        view-label="name"
                        emit-prop="id"
                        empty-text="No workers found"
                        width="100%"
                    >
                        <template #default="{ item }">
                            {{ item.name }}
                        </template>
                    </CustomSelect>
                </FormField>
            </Column>
            <Column :span="4">
                <FormField name="languages" label="Language" :error="error">
                    <CustomSelect
                        v-model="languages"
                        :options="workersLanguages"
                        with-filter
                        placeholder="Select language"
                        view-label="name"
                        emit-prop="id"
                        empty-text="No languages found"
                        width="100%"
                    >
                        <template #default="{ item }">
                            {{ item.name }}
                        </template>
                    </CustomSelect>
                </FormField>
            </Column>
            <Column :span="4">
                <FormField name="user" label="User" :error="error" required>
                    <PaginatedResource
                        ref="workersResource"
                        :page-size="200"
                        :fetch-method="workersFactory.fetchUsers"
                        :filter-fields="['customer_id', 'role', 'languages', 'users_id_not']"
                        delay-fetch
                    >
                        <template #default="{ loading, resource: users }">
                            <CustomSelect
                                v-model="worker"
                                :form-value-text="getUserName"
                                :options="users"
                                :disabled="loading || !role"
                                :popper-options="{ placement: 'top' }"
                                paginate
                                :paginate-amount="10"
                                empty-text="No users found"
                                placeholder="Select a user"
                                with-filter
                                width="100%"
                            >
                                <template #default="{ item }">
                                    {{ getUserName(item) }}
                                </template>
                            </CustomSelect>
                        </template>
                    </PaginatedResource>
                </FormField>
                <button
                    class="button button--medium button--round button--fullwidth"
                    :class="[{ 'is-loading': loading, 'is-disabled': !canAdd }]"
                    :disabled="!canAdd"
                >
                    Add
                </button>
            </Column>
        </Grid>
    </form>
</template>

<script>
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import { FormField, CustomSelect } from '@speechki-frontend/shared/ui/forms';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';

import {
    addWorkerToOrder,
    fetchCustomerMembers,
    fetchCustomersEmployees,
    fetchCustomersMembersLanguages,
    fetchEmployeesLanguages,
} from '../../api/workers';

import { mapActions } from 'vuex';

const WORKERS_API = {
    customer_member: {
        fetchUsers: fetchCustomerMembers,
        fetchLanguages: fetchCustomersMembersLanguages,
    },
    employee: {
        fetchUsers: fetchCustomersEmployees,
        fetchLanguages: fetchEmployeesLanguages,
    },
};

export default {
    name: 'OrderManageWorkersAdd',
    components: {
        Grid,
        Column,
        FormField,
        CustomSelect,
        PaginatedResource,
    },
    props: {
        order: {
            type: Object,
            required: true,
        },
        workers: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            worker: undefined,
            role: undefined,
            languages: this.order.details.book_language,

            error: null,
            loading: false,
            workersLanguages: [],
        };
    },

    computed: {
        workerResource() {
            return this.order.manage_worker_info.type_worker;
        },
        workersFactory() {
            const { [this.workerResource]: factory } = WORKERS_API;
            if (!factory) {
                throw 'Invalid worker resource';
            }

            return factory;
        },
        orderRoles() {
            if (!this.order.actual_roles) return [];
            return Object.entries(this.order.actual_roles).map(([id, name]) => ({ name, id }));
        },
        workersExcluded() {
            return this.workers
                .filter(({ role }) => role.slug == this.role)
                .map(({ user_id }) => user_id.id);
        },
        canAdd() {
            return !(this.loading || !this.role);
        },
    },

    watch: {
        async role() {
            if (!this.languages) return;
            this.worker = undefined;
            await this.$nextTick();
            this.fetchWorkersResource();
        },
        languages() {
            if (!this.role) return;
            this.worker = undefined;
            this.fetchWorkersResource();
        },
        workers() {
            if (this.role === undefined) return;
            this.fetchWorkersResource();
        },
        orderRoles: {
            immediate: true,
            handler(roles) {
                if (this.role || !roles.length) return;
                this.role = roles[0].id;
            },
        },
        workersLanguages: {
            immediate: true,
            handler(languages) {
                if (this.languages || !languages.length) return;
                this.languages = languages[0].id;
            },
        },
    },

    async created() {
        try {
            this.loading = true;
            this.error = null;

            await this.fetchLanguages();
        } catch (e) {
            this.error = e;
            this.pushToast({
                id: Date.now(),
                type: 'error',
                title: 'Error',
            });
        } finally {
            this.loading = false;
        }
    },

    methods: {
        ...mapActions(['pushToast']),
        async fetchWorkersResource() {
            this.loading = true;
            await this.$refs.workersResource.filter({
                role: this.role,
                languages: this.languages,
                customer_id: this.order.customer.id,
                users_id_not: this.workersExcluded.join(','),
            });
            this.loading = false;
        },
        async addWorker() {
            try {
                this.loading = true;
                this.error = null;

                const { id } = this.order;
                const data = {
                    user_id: this.worker.user.id,
                    role: [this.role],
                };
                await addWorkerToOrder(id, data);

                this.worker = undefined;
                this.role = undefined;
                this.$emit('create');
            } catch (e) {
                this.error = e;
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: 'Error',
                    text: e.non_field_errors ? e.non_field_errors[0] : 'Please try again',
                });
            } finally {
                this.loading = false;
            }
        },
        async fetchLanguages() {
            const results = await this.workersFactory.fetchLanguages();
            this.workersLanguages = Object.entries(results).map(([id, name]) => ({ name, id }));
        },
        getUserName({ user }) {
            const { full_name: name, email } = user;
            return String(name).trim() || email;
        },
    },
};
</script>
